import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { UserFilterModel, UserModel } from 'src/app/models/User.model';
import { ApiService } from '../API/api.service';
import { DepartmentModel } from 'src/app/models/Department.model';
import { FilterModel } from 'src/app/models/Filter.model';
import { JobsModel } from 'src/app/models/Job.model';
import { BusinessModel } from 'src/app/models/Business.model';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  private collaboratorCacheById = new Map<string, any>();
  private collaboratorInflightById = new Map<string, Observable<any>>();

  constructor(private api: ApiService) {}

  employeeUrl = `employee/api/v1/`;
  employeeUrlV2 = `employee/api/v2/`;

  getUser(id: string): Observable<UserModel> {
    return this.api.get(`${this.employeeUrlV2}Manage/${id}`).pipe(map(collaborators => new UserModel(collaborators)));
  }

  openTeams(email: string) {
    window.location.href = `msteams:/l/chat/0/0?users=${email}`;
  }

  openEmail(email: string) {
    window.location.href = `mailto:${email}`;
  }

  openWhats(tel: string, name: string) {
    const _tel = tel.replace(/[^0-9]/g, '');
    let ddi = '+55' + _tel;

    try {
      const newWindow = window.open(
        `https://api.whatsapp.com/send?phone=${ddi} - ${name}`,
        'janela1',
        'width=800, height=600, directories=no, location=no, menubar=no,scrollbars=no, status=no, toolbar=no, resizable=no',
      );

      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        alert(
          'Não foi possível abrir o link porque o seu navegador está bloqueando pop-ups. Por favor, ative os pop-ups para acessar o link.',
        );
      }
    } catch (e) {
      alert('Não foi possível abrir o link. Verifique as configurações de pop-up do seu navegador.');
    }
  }

  getAllCollaborators(filter: UserFilterModel): Observable<UserModel[]> {
    return this.api
      .get(`${this.employeeUrl}Employees`, filter)
      .pipe(map((collaborators: any) => collaborators.map((c: UserModel) => new UserModel(c))));
  }

  getCollaborator(id: string): Observable<any> {
    const cached = this.collaboratorCacheById.get(id);
    if (cached) {
      return of(cached);
    }

    const inflight = this.collaboratorInflightById.get(id);
    if (inflight) {
      return inflight;
    }

    const request$ = this.api.get(`${this.employeeUrl}Employees/${id}`).pipe(
      map(employee => employee),
      tap(employee => {
        this.collaboratorCacheById.set(id, employee);
      }),
      finalize(() => {
        this.collaboratorInflightById.delete(id);
      }),
      shareReplay(1),
    );

    this.collaboratorInflightById.set(id, request$);
    return request$;
  }

  getDepartment(filter: FilterModel): Observable<DepartmentModel[]> {
    return this.api
      .get(`${this.employeeUrl}Departaments`, filter)
      .pipe(map((departments: any) => departments.map((d: DepartmentModel) => new DepartmentModel(d))));
  }

  getJobs(filter: FilterModel): Observable<JobsModel[]> {
    return this.api
      .get(`${this.employeeUrl}Jobs`, filter)
      .pipe(map((jobs: any) => jobs.map((j: JobsModel) => new JobsModel(j))));
  }

  getBusinessUnits(filter: FilterModel): Observable<BusinessModel[]> {
    return this.api.get(`${this.employeeUrl}BusinessUnits`, filter).pipe(map((BusinessUnits: any) => BusinessUnits));
  }
}
